import { useState } from "react";

import { getExtensionValue } from "@medplum/core";
import { CodeableConcept, QuestionnaireItem } from "@medplum/fhirtypes";

import { Alert } from "@mantine/core";
import type { UseFormReturnType } from "@mantine/form";

import { IconExclamationCircle } from "@tabler/icons-react";

import { TextWrapper } from "@/components/TextWrapper";
import { useBaselineFhrRefresh } from "@/encounters/components/EncounterLabor/contexts/BaselineFhrRefreshContext";
import { BaselineFhr } from "@/encounters/components/EncounterLabor/EncounterLabor/LaborFlowsheetComponents/BaselineFHR";
import { Extensions } from "@/fhir/enums/extensions";

type QuestionnaireDisplayProps<T = unknown> = {
  value: string;
  questionnaireItem?: QuestionnaireItem;
  form?: UseFormReturnType<T>;
};

enum FormControlType {
  BaselineFHR = "baseline-fhr",
}

function QuestionnaireDisplay<T = unknown>({
  value,
  questionnaireItem,
}: QuestionnaireDisplayProps<T>) {
  const [isAlertVisible, setIsAlertVisible] = useState(true);
  const { refreshWhen, baselineRefreshKey } = useBaselineFhrRefresh();

  const alertExtension = getExtensionValue(
    questionnaireItem,
    Extensions.QuestionnaireItemAlert,
  ) as string;

  const formControlCodeableConcept = getExtensionValue(
    questionnaireItem,
    Extensions.QuestionnaireItemControl,
  ) as CodeableConcept | undefined;
  const formControlType = formControlCodeableConcept?.coding?.[0]?.code;

  if (formControlType === FormControlType.BaselineFHR) {
    return <BaselineFhr refreshWhen={refreshWhen} baselineRefreshKey={baselineRefreshKey} />;
  }

  if (alertExtension) {
    return (
      <Alert
        color="blue"
        title={value}
        withCloseButton
        variant="light"
        hidden={!isAlertVisible}
        icon={<IconExclamationCircle />}
        onClose={() => setIsAlertVisible(false)}
      >
        <TextWrapper>{alertExtension}</TextWrapper>
      </Alert>
    );
  }

  return <TextWrapper>{value}</TextWrapper>;
}

export default QuestionnaireDisplay;
